import { useState } from "react";
import "./PaymentRequiredModal.css";

const PAYMENT_METHODS = [
  {
    id: "cash",
    label: "Cash",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="6" width="20" height="12" rx="2" />
        <circle cx="12" cy="12" r="2" />
        <path d="M6 12h.01M18 12h.01" />
      </svg>
    ),
  },
    {
      id: "card",
      label: "Bank Card",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="4" width="20" height="16" rx="2" />
          <path d="M2 10h20M6 15h3" />
        </svg>
      ),
    },
];

const CONSULTATION_FEE = 70;

export default function PaymentRequiredModal({
  patient,
  onClose,
  onConfirm,
  onReschedule,
  mode = "payment",
  defaultConsultationPrice = CONSULTATION_FEE,
  defaultMedicalActs = 0,
  alreadyPaid = 0,
  initialPaymentHistory = [],
}) {
  const [selectedMethod, setSelectedMethod] = useState("cash");
  const [consultationFeeInput, setConsultationFeeInput] = useState(
    String(Number(defaultConsultationPrice) > 0 ? defaultConsultationPrice : CONSULTATION_FEE),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const consultationFee = Number(consultationFeeInput) || 0;
  const medicalActsFee = Number(defaultMedicalActs) || 0;
  const totalFee = consultationFee + medicalActsFee;
  const remaining = Math.max(0, totalFee - Number(alreadyPaid || 0));
  const finalAmount = remaining;

  const handleConfirm = async () => {
    if (!consultationFee || !finalAmount || isSubmitting) return;
    const isPaid = Number(alreadyPaid || 0) + finalAmount >= totalFee - 0.001;
    const paymentEntry = {
      amount: finalAmount,
      method: selectedMethod,
      paidAt: new Date().toISOString(),
    };
    const paymentData = {
      paymentStatus: isPaid ? "paid" : "partial",
      amount: finalAmount,
      paymentType: isPaid ? "full" : "installment",
      paymentMethod: selectedMethod,
      consultationPrice: consultationFee,
      medicalActsTotal: medicalActsFee,
      paymentEntry,
      paymentHistory: initialPaymentHistory,
      totalAmount: totalFee,
    };

    setIsSubmitting(true);
    setSubmitError("");
    try {
      await onConfirm(paymentData);
    } catch (error) {
      console.error("Erreur lors de l'enregistrement du paiement:", error);
      setSubmitError(
        error?.message ||
        error?.data?.message ||
        "Le paiement n'a pas pu être enregistré. Réessayez.",
      );
      setIsSubmitting(false);
    }
  };

  return (
    <div className="prm__overlay" onClick={(e) => !isSubmitting && e.target === e.currentTarget && onClose()}>
      <div className="prm__modal">
        <div className="prm__header">
          <div>
            <h2 className="prm__title">Payment Required</h2>
            <p className="prm__subtitle">
              Collect payment for <strong>{patient.name}</strong>'s consultation
            </p>
          </div>
          <button className="prm__close" onClick={onClose} aria-label="Close" disabled={isSubmitting}>×</button>
        </div>

        <>
            <div className="prm__fee-box">
              <span className="prm__fee-label">Total consultation · Remaining</span>
              <span className="prm__fee-amount">{totalFee.toFixed(3)} · {remaining.toFixed(3)} DT</span>
            </div>

            <div className="prm__section">
              <p className="prm__section-title">Payment Type</p>
              <div className="prm__types">
                <div className="prm__type-card prm__type-card--active">
                  <div className="prm__type-top">
                    <span className="prm__type-label">Full Payment</span>
                    <span className="prm__type-tag prm__type-tag--standard">Standard</span>
                  </div>
                  <p className="prm__type-desc">Full consultation fee</p>
                  <div className="prm__custom-input-wrap">
                    <span className="prm__custom-prefix">DT</span>
                    <input
                      className="prm__custom-input"
                      type="number"
                      min="0.001"
                      step="0.001"
                      value={consultationFeeInput}
                      onChange={(event) => setConsultationFeeInput(event.target.value)}
                      disabled={isSubmitting}
                      aria-label="Full payment consultation fee"
                    />
                  </div>
                </div>
              </div>
            </div>

            <p className="prm__custom-hint prm__section">
              Medical acts are added to the consultation fee.
            </p>

            <div className="prm__section">
              <p className="prm__section-title">Payment Method</p>
              <div className="prm__methods">
                {PAYMENT_METHODS.map((method) => (
                  <button
                    key={method.id}
                    className={`prm__method-btn ${selectedMethod === method.id ? "prm__method-btn--active" : ""}`}
                    onClick={() => setSelectedMethod(method.id)}
                    disabled={isSubmitting}
                  >
                    <span className="prm__method-icon">{method.icon}</span>
                    <span>{method.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="prm__footer">
              <div className="prm__total">
                <span className="prm__total-label">Amount to Collect · {remaining.toFixed(3)} DT remaining</span>
                <span className="prm__total-amount">{finalAmount.toFixed(3)} DT</span>
              </div>
              {submitError && <p className="prm__error" role="alert">{submitError}</p>}
              {onReschedule && (
                <button className="prm__reschedule-btn" onClick={() => onReschedule(patient)} disabled={isSubmitting}>
                  Reschedule appointment
                </button>
              )}
              <button
                className="prm__confirm-btn"
                onClick={handleConfirm}
                disabled={!consultationFee || !finalAmount || isSubmitting}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" /><polyline points="9 12 11 14 15 10" />
                </svg>
                {isSubmitting
                  ? "Saving payment..."
                  : mode === "completion"
                  ? "Complete consultation"
                  : "Confirm Payment"}
              </button>
            </div>
        </>
      </div>
    </div>
  );
}